import { useCallback, useEffect, useState } from 'react';
import { HistoryOutlined } from '@ant-design/icons';
import { IGuide, IGuideLogs } from '../../../shared/interfaces';
import { useToast } from '../../../hooks/toast';
import { Button, Card, Empty, Modal, Table, Tag, Typography } from 'antd';
import { formatDate } from '../../../shared/utils/format-date';
import { getGuideLogs } from '../../../http';

const { Text } = Typography;

interface IHistoricGuideProps {
  open: boolean;
  close: () => void;
  guide: IGuide | null;
}

const HistoricGuide: React.FC<IHistoricGuideProps> = ({
  open,
  close,
  guide,
}) => {
  const { addToast } = useToast();
  const [historics, setHistorics] = useState<IGuideLogs[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const listHistoric = useCallback(async () => {
    if (!guide?.id) {
      setHistorics([]);
      return;
    }

    setLoading(true);
    await getGuideLogs(guide?.id)
      .then((data) => setHistorics(data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Erro ao listar histórico da guia',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [guide?.id, addToast]);

  useEffect(() => {
    if (!open) {
      return;
    }

    listHistoric();
  }, [open, listHistoric]);

  return (
    <Modal
      title={
        <>
          <HistoryOutlined /> Histórico da Guia Nº {guide?.code}
        </>
      }
      centered
      open={open}
      cancelText="Cancelar"
      onCancel={close}
      width={900}
      footer={[
        <Button key="back" onClick={close}>
          Fechar
        </Button>,
      ]}
    >
      <Card
        size="small"
        style={{ marginBottom: 12 }}
        bodyStyle={{ padding: '10px 12px' }}
      >
        <Text type="secondary">
          Acompanhe abaixo todas as alterações de status e observações da guia.
        </Text>
      </Card>

      <Table
        scroll={{ y: 510 }}
        loading={loading}
        columns={[
          {
            title: 'Data',
            dataIndex: 'date',
            render: (value: string) => <Text strong>{formatDate(value)}</Text>,
            width: 120,
          },
          {
            title: 'Hora',
            dataIndex: 'time',
            width: 90,
            render: (value: string) => value || '-',
          },
          {
            title: 'Status',
            dataIndex: 'description_status',
            render: (value: string) => (
              <Tag color="blue" style={{ marginRight: 0, fontWeight: 600 }}>
                {value || 'Não informado'}
              </Tag>
            ),
          },
          {
            title: 'Usuário',
            dataIndex: 'user_name',
            render: (value: string) => value || '-',
          },
          {
            title: 'Observação',
            dataIndex: 'observation',
            render: (value: string) => value || '-',
          },
        ]}
        dataSource={historics}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nenhum histórico registrado para esta guia."
            />
          ),
        }}
      />
    </Modal>
  );
};

export default HistoricGuide;
